import React from 'react';
import { CheckIcon, PlayIcon, UserIcon } from '@phosphor-icons/react';
import { QwenPresetSpeaker } from '../../types';
import { PRESET_SPEAKERS } from '../../lib/characterVoiceService';

interface PresetSpeakerPickerProps {
  value?: QwenPresetSpeaker;
  onChange: (speaker: QwenPresetSpeaker) => void;
  onPreviewSpeaker?: (speaker: QwenPresetSpeaker) => void;
  isPreviewLoading?: boolean;
  previewingSpeaker?: QwenPresetSpeaker;
  className?: string;
}

export const PresetSpeakerPicker: React.FC<PresetSpeakerPickerProps> = ({
  value,
  onChange,
  onPreviewSpeaker,
  isPreviewLoading = false,
  previewingSpeaker,
  className = '',
}) => {
  // Group speakers by language region
  const englishSpeakers = PRESET_SPEAKERS.filter(s => s.language.includes('English'));
  const chineseSpeakers = PRESET_SPEAKERS.filter(s => s.language.includes('Chinese'));
  const asianSpeakers = PRESET_SPEAKERS.filter(s => s.language === 'Japanese' || s.language === 'Korean');

  const renderSpeakerCard = (speaker: typeof PRESET_SPEAKERS[number]) => {
    const isSelected = value === speaker.id;
    const isLoading = isPreviewLoading && previewingSpeaker === speaker.id;

    return (
      <button
        key={speaker.id}
        onClick={() => onChange(speaker.id as QwenPresetSpeaker)}
        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
          isSelected
            ? 'border-slate-blue bg-slate-blue/10'
            : 'border-graphite bg-carbon hover:border-iron'
        }`}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-slate-blue flex items-center justify-center">
            <CheckIcon className="w-3 h-3 text-white" weight="bold" />
          </div>
        )}

        {/* Speaker icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
          isSelected ? 'bg-slate-blue/20' : 'bg-graphite'
        }`}>
          <UserIcon className={`w-5 h-5 ${isSelected ? 'text-slate-blue' : 'text-iron'}`} />
        </div>

        {/* Speaker name */}
        <h4 className="font-medium text-sm text-white mb-1">{speaker.name}</h4>

        {/* Description */}
        <p className="text-xs text-dust/50 line-clamp-2">{speaker.description}</p>

        {/* Language badge */}
        <span className={`inline-block mt-2 px-2 py-0.5 rounded text-[10px] ${
          isSelected ? 'bg-slate-blue/20 text-slate-blue-soft' : 'bg-graphite text-dust/40'
        }`}>
          {speaker.language}
        </span>

        {/* Preview button */}
        {onPreviewSpeaker && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreviewSpeaker(speaker.id as QwenPresetSpeaker);
            }}
            disabled={isLoading}
            className={`absolute bottom-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              isLoading
                ? 'bg-graphite cursor-wait'
                : 'bg-slate-blue/20 hover:bg-slate-blue/30'
            }`}
          >
            {isLoading ? (
              <div className="w-3 h-3 rounded-full border-2 border-slate-blue/30 border-t-slate-blue animate-spin" />
            ) : (
              <PlayIcon className="w-3.5 h-3.5 text-slate-blue" weight="fill" />
            )}
          </button>
        )}
      </button>
    );
  };

  return (
    <div className={className}>
      {/* English Speakers */}
      {englishSpeakers.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-medium text-dust/40 uppercase tracking-wider mb-3">English</h3>
          <div className="grid grid-cols-4 gap-3">
            {englishSpeakers.map(renderSpeakerCard)}
          </div>
        </div>
      )}

      {/* Chinese Speakers */}
      {chineseSpeakers.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-medium text-dust/40 uppercase tracking-wider mb-3">Chinese</h3>
          <div className="grid grid-cols-4 gap-3">
            {chineseSpeakers.map(renderSpeakerCard)}
          </div>
        </div>
      )}

      {/* Japanese & Korean Speakers */}
      {asianSpeakers.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-dust/40 uppercase tracking-wider mb-3">Japanese & Korean</h3>
          <div className="grid grid-cols-4 gap-3">
            {asianSpeakers.map(renderSpeakerCard)}
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetSpeakerPicker;
